// @oasis-permission-inherits: /projects (projects:view)
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { addDays, endOfDay, format, startOfDay, subDays } from "date-fns";
import { Copy, ExternalLink, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DateRangePicker } from "~/components/ui/date-range-picker";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useAuthStore } from "~/modules/auth/auth-store";
import { searchProjectsQuery } from "~/modules/projects/queries";
import {
  projectSearchProductTypeSchema,
  projectSearchProductTypes,
  type ProjectSearchEditState,
  type ProjectSearchItem,
  type ProjectSearchProductType,
} from "~/modules/projects/schemas";

const DEFAULT_PROJECT_SEARCH_PRODUCT_TYPE = "LONG_TO_SHORT";
const DEFAULT_PROJECT_SEARCH_PAGE_SIZE = 50;

interface ProjectSearchDateRange {
  from: Date;
  to: Date;
}

export const Route = createFileRoute("/_dash/projects/search")({
  validateSearch: z.object({
    productType: projectSearchProductTypeSchema
      .catch(DEFAULT_PROJECT_SEARCH_PRODUCT_TYPE)
      .default(DEFAULT_PROJECT_SEARCH_PRODUCT_TYPE),
    updatedAfterMs: z.number().int().nonnegative().optional().catch(undefined),
    updatedBeforeMs: z.number().int().nonnegative().optional().catch(undefined),
  }),
  beforeLoad({ search }) {
    const normalizedDateRange = getNormalizedDateRange(
      search.updatedAfterMs,
      search.updatedBeforeMs
    );
    const normalizedUpdatedAfterMs = normalizedDateRange.from.getTime();
    const normalizedUpdatedBeforeMs = normalizedDateRange.to.getTime();

    if (
      search.updatedAfterMs === normalizedUpdatedAfterMs &&
      search.updatedBeforeMs === normalizedUpdatedBeforeMs
    ) {
      return;
    }

    throw redirect({
      to: "/projects/search",
      replace: true,
      search: {
        productType: search.productType,
        updatedAfterMs: normalizedUpdatedAfterMs,
        updatedBeforeMs: normalizedUpdatedBeforeMs,
      },
    });
  },
  component: RouteComponent,
});

function getDefaultDateRange(): ProjectSearchDateRange {
  return {
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  };
}

function getNormalizedDateRange(
  updatedAfterMs: number | undefined,
  updatedBeforeMs: number | undefined
): ProjectSearchDateRange {
  if (updatedAfterMs != null && updatedBeforeMs != null) {
    const from = startOfDay(new Date(updatedAfterMs));
    const to = endOfDay(new Date(updatedBeforeMs));

    if (from <= to) {
      return { from, to };
    }

    return {
      from: startOfDay(to),
      to: endOfDay(from),
    };
  }

  if (updatedAfterMs != null) {
    const from = startOfDay(new Date(updatedAfterMs));

    return {
      from,
      to: endOfDay(addDays(from, 7)),
    };
  }

  if (updatedBeforeMs != null) {
    const to = endOfDay(new Date(updatedBeforeMs));

    return {
      from: startOfDay(subDays(to, 7)),
      to,
    };
  }

  return getDefaultDateRange();
}

function getSearchDateRange(
  updatedAfterMs: number | undefined,
  updatedBeforeMs: number | undefined
): ProjectSearchDateRange {
  if (updatedAfterMs == null || updatedBeforeMs == null) {
    return getDefaultDateRange();
  }

  return {
    from: new Date(updatedAfterMs),
    to: new Date(updatedBeforeMs),
  };
}

function getDateRangeLabel(range: DateRange | undefined) {
  if (!range?.from || !range.to) {
    return "Select a complete range";
  }

  return `${format(range.from, "LLL dd, y")} - ${format(range.to, "LLL dd, y")}`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getStateBadgeVariant(state: ProjectSearchEditState) {
  if (state === "COMPLETE") {
    return "default";
  }

  if (state === "FAILED" || state === "CANCELED") {
    return "destructive";
  }

  if (state === "PROCESSING") {
    return "secondary";
  }

  return "outline";
}

async function copyToClipboard(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Failed to copy");
  }
}

function CopyIdButton({ value }: { value: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          aria-label="Copy ID"
          onClick={() => void copyToClipboard(value)}
        >
          <Copy className="size-3" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Copy ID</TooltipContent>
    </Tooltip>
  );
}

function IdValue({ value }: { value: string | null | undefined }) {
  if (!value) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="flex min-w-40 items-center gap-1">
      <span className="truncate font-mono text-xs" title={value}>
        {value}
      </span>
      <CopyIdButton value={value} />
    </div>
  );
}

function RenderedProjectValue({
  canOpenProject,
  item,
}: {
  canOpenProject: boolean;
  item: ProjectSearchItem;
}) {
  if (!item.renderedProjectId) {
    return <IdValue value={null} />;
  }

  if (!canOpenProject || item.editState !== "COMPLETE") {
    return <IdValue value={item.renderedProjectId} />;
  }

  return (
    <div className="flex min-w-40 items-center gap-1">
      <Link
        to="/projects/$projectId"
        params={{ projectId: item.renderedProjectId }}
        className="inline-flex min-w-0 items-center gap-1 font-mono text-xs text-primary hover:underline"
      >
        <span className="truncate" title={item.renderedProjectId}>
          {item.renderedProjectId}
        </span>
        <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
      </Link>
      <CopyIdButton value={item.renderedProjectId} />
    </div>
  );
}

function ProjectSearchResultsTable({
  canOpenProjects,
  items,
}: {
  canOpenProjects: boolean;
  items: ProjectSearchItem[];
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Project</TableHead>
          <TableHead>Product</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Updated</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Rendered project</TableHead>
          <TableHead>Video</TableHead>
          <TableHead>Operation</TableHead>
          <TableHead>Error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item, index) => (
          <TableRow key={`${item.projectId}-${item.operationId ?? item.workflowId ?? index}`}>
            <TableCell>
              <div className="max-w-56 whitespace-normal">
                <div className="font-medium">{item.projectTitle ?? "Untitled project"}</div>
                <div className="mt-1 text-muted-foreground">
                  <IdValue value={item.projectId} />
                </div>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline">{item.productType}</Badge>
            </TableCell>
            <TableCell>
              <Badge variant={getStateBadgeVariant(item.editState)}>{item.editState}</Badge>
            </TableCell>
            <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
            <TableCell>{formatDateTime(item.createdAt)}</TableCell>
            <TableCell>
              <RenderedProjectValue canOpenProject={canOpenProjects} item={item} />
            </TableCell>
            <TableCell>
              <IdValue value={item.videoId} />
            </TableCell>
            <TableCell>
              <IdValue value={item.operationId ?? item.workflowId} />
            </TableCell>
            <TableCell className="max-w-80 whitespace-normal text-xs">
              {item.errorCode || item.error ? (
                <div>
                  {item.errorCode && <div className="font-medium">{item.errorCode}</div>}
                  {item.error && <div className="mt-1 text-muted-foreground">{item.error}</div>}
                </div>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RouteComponent() {
  const { productType, updatedAfterMs, updatedBeforeMs } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [user] = useAuthStore((s) => [s.user]);
  const canViewProjects = user?.permissions.includes("projects:view") ?? false;
  const selectedDateRange = useMemo(
    () => getSearchDateRange(updatedAfterMs, updatedBeforeMs),
    [updatedAfterMs, updatedBeforeMs]
  );
  const queryParams = useMemo(
    () => ({
      productType,
      updatedAfterMs: startOfDay(selectedDateRange.from).getTime(),
      updatedBeforeMs: endOfDay(selectedDateRange.to).getTime(),
      pageSize: DEFAULT_PROJECT_SEARCH_PAGE_SIZE,
    }),
    [productType, selectedDateRange]
  );
  const [productTypeInput, setProductTypeInput] = useState<ProjectSearchProductType>(productType);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(selectedDateRange);
  const { data, error, isFetching, isLoading, refetch } = searchProjectsQuery.useQuery({
    params: queryParams,
  });
  const items = data?.items ?? [];
  const isInitialLoading = isLoading && !data;
  let resultsStatus = `${items.length} projects`;
  if (isInitialLoading) {
    resultsStatus = "Searching";
  } else if (error) {
    resultsStatus = "Search failed";
  } else if (isFetching) {
    resultsStatus = "Refreshing";
  }

  useEffect(() => {
    setProductTypeInput(productType);
  }, [productType]);

  useEffect(() => {
    setDateRange(selectedDateRange);
  }, [selectedDateRange]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!dateRange?.from || !dateRange.to) {
      return;
    }

    const updatedAfterMs = startOfDay(dateRange.from).getTime();
    const updatedBeforeMs = endOfDay(dateRange.to).getTime();

    if (updatedAfterMs > updatedBeforeMs) {
      return;
    }

    const productTypeSelect = event.currentTarget.elements.namedItem("productType");
    const nextProductType =
      productTypeSelect instanceof HTMLSelectElement
        ? projectSearchProductTypeSchema.parse(productTypeSelect.value)
        : productTypeInput;
    const nextSearch = {
      productType: nextProductType,
      updatedAfterMs,
      updatedBeforeMs,
    };

    if (
      queryParams.productType === nextSearch.productType &&
      queryParams.updatedAfterMs === nextSearch.updatedAfterMs &&
      queryParams.updatedBeforeMs === nextSearch.updatedBeforeMs
    ) {
      void refetch();
      return;
    }

    navigate({
      search: nextSearch,
    });
  };

  const resetFilters = () => {
    const nextDateRange = getDefaultDateRange();
    setProductTypeInput(DEFAULT_PROJECT_SEARCH_PRODUCT_TYPE);
    setDateRange(nextDateRange);
    navigate({
      search: {
        productType: DEFAULT_PROJECT_SEARCH_PRODUCT_TYPE,
        updatedAfterMs: nextDateRange.from.getTime(),
        updatedBeforeMs: nextDateRange.to.getTime(),
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Projects Search</h1>
          <p className="text-sm text-muted-foreground">Submitted edit projects for {productType}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/projects">All Projects</Link>
          </Button>
        </div>
      </div>

      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <div className="grid gap-2">
          <Label htmlFor="project-search-product-type">Product type</Label>
          <select
            id="project-search-product-type"
            name="productType"
            className="h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            value={productTypeInput}
            onChange={(event) =>
              setProductTypeInput(event.target.value as ProjectSearchProductType)
            }
          >
            {projectSearchProductTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-2">
          <Label>Updated time range</Label>
          <DateRangePicker value={dateRange} onChange={(range) => setDateRange(range)} />
        </div>

        <Button type="submit" className="gap-1.5">
          <Search className="size-4" aria-hidden="true" />
          Search
        </Button>
        <Button type="button" variant="outline" onClick={resetFilters}>
          Reset
        </Button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">{getDateRangeLabel(selectedDateRange)}</div>
        <div className="text-sm text-muted-foreground">{resultsStatus}</div>
      </div>

      {isInitialLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load project search results"}
        </div>
      )}

      {!isInitialLoading && items.length > 0 && (
        <ProjectSearchResultsTable canOpenProjects={canViewProjects} items={items} />
      )}

      {!isInitialLoading && !error && items.length === 0 && (
        <div className="rounded-md border bg-muted/20 p-6 text-sm text-muted-foreground">
          No submitted edit projects found for this search.
        </div>
      )}
    </div>
  );
}

export { DEFAULT_PROJECT_SEARCH_PAGE_SIZE, DEFAULT_PROJECT_SEARCH_PRODUCT_TYPE };
